import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import contractRefundPaymentEmailTemplateDeleteApi from 'models/core/contractRefundPaymentEmailTemplate/delete/Api.ContractRefundPaymentEmailTemplate.Delete';
import ContractRefundPaymentEmailTemplate from 'models/core/contractRefundPaymentEmailTemplate/Model.ContractRefundPaymentEmailTemplate';
import contractRefundPaymentEmailTemplateSaveApi from 'models/core/contractRefundPaymentEmailTemplate/save/Api.ContractRefundPaymentEmailTemplate.Save';
import ContractRefundPaymentStatusTriggers from 'models/enums/ContractRefundPaymentStatusTriggers';
import { useMemo } from 'react';
import { contractRefundPaymentEmailTemplateIndexRoute } from './ContractRefundPaymentEmailTemplate.Index';

export enum ContractRefundPaymentEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyContractRefundPaymentEmailTemplateAction = Utils.VerifyExtends<
  Module.Finance.ContractRefundPaymentEmailTemplate.Actions,
  Utils.ValueOf<typeof ContractRefundPaymentEmailTemplateAction>
>;
export default function useContractRefundPaymentEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Finance.ContractRefundPaymentEmailTemplate.Params,
    Module.Finance.ContractRefundPaymentEmailTemplate.Query
  >({
    route: contractRefundPaymentEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ContractRefundPaymentEmailTemplate.list });
  const listApi = useApi({
    action: ContractRefundPaymentEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: contractRefundPaymentEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: contractRefundPaymentEmailTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.contractRefundPaymentStatusTrigger) || [];
    const statuses = Object.values({ ...ContractRefundPaymentStatusTriggers })?.map((status) => status);

    return [
      ...statuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.contractRefundPaymentStatusTrigger],
    ];
  }, [templateApi?.payload?.data, selected]);

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    views: {
      [ContractRefundPaymentEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ContractRefundPaymentEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ContractRefundPaymentEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ContractRefundPaymentEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useContractRefundPaymentEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useContractRefundPaymentEmailTemplate>>();
}
